// @givmo/app-admin — router factory + type registration.
//
// `createAdminRouter` builds a router over the code-based route tree with a PLACEHOLDER context
// (the live `AuthState` and the app's `QueryClient` are injected via
// `<RouterProvider context={{ auth, queryClient }} />` in App). A `history` override is accepted so
// component tests can drive an in-memory router (`createMemoryHistory`).
//
// The module-level `router` instance exists only to feed the `Register` declaration (the typed
// `Link`/`redirect` surface); App passes its own instance to `RouterProvider`.

import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { routeTree } from './routes';
import { NotFound } from '../components/not-found';
import { clearAuthDoorErrors } from '../features/auth/use-auth-error';

export interface CreateAdminRouterOptions {
  /** Override the history (tests pass `createMemoryHistory`). Defaults to the browser history. */
  history?: RouterHistory;
}

export function createAdminRouter(options: CreateAdminRouterOptions = {}) {
  const router = createRouter({
    routeTree,
    // Placeholder — filled per-render by `<RouterProvider context={{ auth, queryClient }} />`.
    context: { auth: undefined!, queryClient: undefined! },
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFound,
    ...(options.history ? { history: options.history } : {}),
    scrollRestoration: true,
  });
  // EP-SUITE2-FIX6: a door's ceremony error is held above the router tree so the identity boundary's
  // remount cannot lose it (`use-auth-error.ts`), which makes the VISIT its lifetime: it clears when
  // this router goes to a different location — never on the boundary's own reloads of the same one
  // (`hrefChanged` false), which is exactly when a ceremony's error arrives.
  router.subscribe('onBeforeNavigate', (event) => {
    if (event.hrefChanged) clearAuthDoorErrors();
  });
  return router;
}

/** The typed router instance used for the `Register` declaration below. */
export const router = createAdminRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
